import React, { useEffect, useRef, useState } from "react";
import { Designer } from "@pdfme/ui";
import { BLANK_PDF } from "@pdfme/common";
import {
  multiVariableText,
  text,
  image,
  svg,
  rectangle,
  ellipse,
  line,
  table,
  dateTime,
  date,
  time,
  select,
  checkbox,
  radioGroup,
} from "@pdfme/schemas";

const fallbackTemplate = {
  basePdf: {
    width: 210,
    height: 297,
    padding: [20, 10, 20, 10],
  },
  schemas: [[]],
};

const baseUrl = "https://pdf-engine-api.edrms.co.za";

const plugins = {
  multiVariableText,
  text,
  image,
  svg,
  line,
  table,
  dateTime,
  rectangle,
  ellipse,
  date,
  time,
  select,
  checkbox,
  radioGroup,
};

const getAuthHeader = () => {
  let stored = localStorage.getItem("auth");
  if (!stored) {
    const username = window.prompt("Enter username:");
    const password = window.prompt("Enter password:");
    const token = btoa(`${username}:${password}`);
    localStorage.setItem("auth", token);
    stored = token;
  }
  return `Basic ${stored}`;
};

const DesignerComponent = () => {
  const containerRef = useRef(null);
  const designerRef = useRef(null);

  const [templateNames, setTemplateNames] = useState([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [template, setTemplate] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState("");

  const latestTemplateRef = useRef(null);

  useEffect(() => {
    fetch(`${baseUrl}/templates`, {
      headers: { Authorization: getAuthHeader() },
    })
      .then((res) => res.json())
      .then((names) => {
        setTemplateNames(names);
        if (names.length > 0) {
          setSelectedTemplateName(names[0]);
        }
      })
      .catch(() =>
        alert("Failed to load templates. Check credentials or server.")
      );
  }, []);

  useEffect(() => {
    if (!selectedTemplateName) return;
    fetch(`${baseUrl}/template/${selectedTemplateName}`, {
      headers: { Authorization: getAuthHeader() },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Template not found");
        return res.json();
      })
      .then((data) => setTemplate(data))
      .catch(() => setTemplate(fallbackTemplate));
  }, [selectedTemplateName]);

  useEffect(() => {
    if (containerRef.current && template) {
      const designer = new Designer({
        domContainer: containerRef.current,
        template,
        plugins,
        options: {
          sidebarOpen: true,
          zoomLevel: 1,
          uiOptions: { hidePagination: false },
        },
      });
      designerRef.current = designer;

      designer.onChangeTemplate((newTemplate) => {
        latestTemplateRef.current = newTemplate;
      });
    }

    return () => designerRef.current?.destroy();
  }, [template]);

  const handleSave = () => {
    const updatedTemplate =
      latestTemplateRef.current || designerRef.current?.getTemplate();
    if (!updatedTemplate || !selectedTemplateName) return;

    if (!updatedTemplate.basePdf) {
      alert("Template is missing a PDF. Please upload one before saving.");
      return;
    }

    fetch(`${baseUrl}/template/${selectedTemplateName}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify(updatedTemplate),
    })
      .then((res) => {
        if (res.ok) {
          alert("Template saved!");
          setTemplate(updatedTemplate);
        } else throw new Error();
      })
      .catch(() => alert("Failed to save template."));
  };

  const createNewTemplate = async (newName) => {
    if (!newName || templateNames.includes(newName)) {
      alert("Template name exists or is invalid.");
      return;
    }
    const blankTemplate = fallbackTemplate;
    const res = await fetch(`${baseUrl}/template/${newName}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify(blankTemplate),
    });
    if (res.ok) {
      alert(`Template "${newName}" created!`);
      setTemplateNames((prev) => [...prev, newName]);
      setSelectedTemplateName(newName);
      setNewTemplateName("");
    } else alert("Failed to create template.");
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const arrayBuffer = reader.result;
      const base64 = btoa(
        new Uint8Array(arrayBuffer)
          .reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      const pdfDataUrl = `data:application/pdf;base64,${base64}`;

      const newTemplate = { ...template, basePdf: pdfDataUrl };
      setTemplate(newTemplate);
      designerRef.current?.updateTemplate(newTemplate);
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>📄 EDRMS PDF Template Designer</h2>
        <button
          className="btn btn-danger"
          onClick={() => {
            localStorage.removeItem("auth");
            window.location.reload();
          }}
        >
          🔒 Logout
        </button>
      </div>

      <div className="row mb-3">
        <div className="col-md-4">
          <label className="form-label">Select Template</label>
          <select
            className="form-select"
            value={selectedTemplateName}
            onChange={(e) => setSelectedTemplateName(e.target.value)}
          >
            {templateNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <input
            type="text"
            className="form-control mt-4"
            placeholder="New template name"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <button
            className="btn btn-primary mt-4 w-100"
            onClick={() => createNewTemplate(newTemplateName)}
          >
            ➕ Create New
          </button>
        </div>
        <div className="col-md-2">
          <input
            type="file"
            accept="application/pdf"
            className="form-control mt-4"
            onChange={handlePdfUpload}
          />
        </div>
      </div>

      <div
        ref={containerRef}
        style={{ height: "600px" }}
        className="border rounded mb-3"
      />

      <button className="btn btn-success" onClick={handleSave}>
        💾 Save Template
      </button>
    </div>
  );
};

export default DesignerComponent;